import { useEffect, useState } from 'react';
import { portailApi } from '../../api/resources';
import { tr, trf } from '../../lib/i18n';
import { lienAppel } from '../../lib/portable';
import { C } from '../../theme/palette';
import { adressesDe } from './listes';
import { alerte, bouton, carte, champ, etiquette, ROLES } from './styles';

/** Les deux rôles qu'un responsable donne à son équipe, dits simplement. */
const CHOIX_ROLES = [
  ['EMPLOYE', '👷 Ouvrier : commande sans prix, à faire valider'],
  ['CHEF', '✓ Chef : voit les prix et valide'],
];

const ETATS = {
  ACTIF: '✓ Accès ouvert',
  FERME: '🔒 Accès fermé',
  INVITE: '⏳ Invité',
  EXPIREE: '⌛ Lien expiré',
};

/** Le choix du rôle d'une invitation ; le chef invite des ouvriers seulement. */
function ChoixRole({ value, onChange, label = 'Rôle', ouvriersSeulement = false }) {
  return (
    // Des cases plutôt qu'une liste : le libellé entier se lit sur un
    // téléphone, là où la liste le coupait (« … à faire [valider] »).
    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend style={{ ...etiquette, padding: 0 }}>{label}</legend>
      {CHOIX_ROLES.filter(([cle]) => !ouvriersSeulement || cle === 'EMPLOYE').map(([cle, lib]) => (
        <label
          key={cle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 44,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name={'role-' + label}
            value={cle}
            checked={value === cle}
            onChange={() => onChange(cle)}
            style={{ width: 20, height: 20, flexShrink: 0 }}
          />
          <span>{lib}</span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * L'équipe du client : inviter un ouvrier ou un chef, ou plusieurs à la fois,
 * chez un ou plusieurs fournisseurs, changer le rôle de chacun, fermer un
 * accès. Au responsable ; le chef, lui, invite des ouvriers, sans gérer
 * l'équipe.
 *
 * @param {object} props propriétés
 * @param {string} props.client entreprise cliente
 * @param {string} [props.magasin] nom du magasin, pour le message à partager
 * @param {boolean} [props.responsable] vrai pour le responsable ; faux pour un chef
 * @returns {import('react').ReactElement} l'équipe
 */
export function MonEquipe({ client, magasin, responsable = true }) {
  if (!responsable) {
    return (
      <>
        <h2 style={{ fontSize: 18, margin: '4px 0 2px' }}>👷 Mon équipe</h2>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{client}</div>
        <InvitationGroupee magasin={magasin} ouvriersSeulement apres={() => {}} />
      </>
    );
  }
  return <EquipeDuResponsable client={client} magasin={magasin} />;
}

/** L'équipe, vue du responsable : inviter, changer les rôles, fermer un accès. */
function EquipeDuResponsable({ client, magasin }) {
  const [personnes, setPersonnes] = useState(null);
  const [f, setF] = useState({ prenom: '', nom: '', email: '', role: 'EMPLOYE' });
  const [invitee, setInvitee] = useState(null);
  const [erreur, setErreur] = useState('');
  const [erreurListe, setErreurListe] = useState('');
  const recharge = () =>
    portailApi
      .equipe()
      .then(setPersonnes)
      .catch(() => setPersonnes([]));
  useEffect(() => {
    recharge();
  }, []);
  const invite = async (e) => {
    e.preventDefault();
    if (!f.prenom.trim()) return setErreur('Indiquez au moins le prénom.');
    setErreur('');
    try {
      const r = await portailApi.invite({
        prenom: f.prenom.trim(),
        nom: f.nom.trim() || null,
        email: f.email.trim() || null,
        role: f.role,
      });
      setInvitee(r);
      setF({ prenom: '', nom: '', email: '', role: f.role });
      recharge();
    } catch (err) {
      setErreur(err?.message || 'L’invitation n’est pas partie.');
    }
    return undefined;
  };
  const agit = async (appel) => {
    setErreurListe('');
    try {
      await appel();
    } catch (err) {
      setErreurListe(err?.message || 'Ça n’a pas marché. Réessayez.');
    }
    recharge();
  };
  return (
    <>
      <InvitationGroupee magasin={magasin} apres={recharge} />
      <form style={carte} onSubmit={invite}>
        <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Inviter une personne</h2>
        <p style={{ fontSize: 13, color: C.grey, margin: '0 0 8px', lineHeight: 1.45 }}>
          {trf(
            'Elle reçoit un lien, crée son compte (nom, prénom, mot de passe, portable) et commande pour {0}.',
            client,
          )}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input
            style={champ}
            aria-label="Prénom"
            placeholder="Prénom"
            value={f.prenom}
            onChange={(e) => setF({ ...f, prenom: e.target.value })}
          />
          <input
            style={champ}
            aria-label="Nom"
            placeholder="Nom"
            value={f.nom}
            onChange={(e) => setF({ ...f, nom: e.target.value })}
          />
        </div>
        <input
          style={{ ...champ, marginTop: 8 }}
          type="email"
          aria-label="E-mail"
          placeholder="E-mail (facultatif : sinon envoyez le lien par WhatsApp)"
          value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
        />
        <div style={{ marginTop: 8 }}>
          <ChoixRole value={f.role} onChange={(role) => setF({ ...f, role })} />
        </div>
        {erreur && (
          <div role="alert" style={alerte}>
            {erreur}
          </div>
        )}
        <button type="submit" style={{ ...bouton(C.green), width: '100%', marginTop: 10 }}>
          ✉️ Inviter
        </button>
      </form>
      {invitee && <PartageInvitation invitee={invitee} />}
      <div style={carte}>
        <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Votre équipe</h2>
        {personnes === null && <div style={{ color: C.muted }}>Chargement…</div>}
        {erreurListe && (
          <div role="alert" style={alerte}>
            {erreurListe}
          </div>
        )}
        {(personnes || []).map((p) => {
          const nom = [p.prenom, p.nom].filter(Boolean).join(' ') || p.email || '—';
          const equipier = !p.invitation && p.role !== 'RESPONSABLE';
          return (
            <div
              key={(p.invitation ? 'i' : 'c') + p.id}
              style={{ padding: '10px 0', borderBottom: '1px solid ' + C.border }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>
                    {nom}
                    {ROLES[p.role] && (
                      <span style={{ fontSize: 11.5, color: C.teal }}>
                        {' · ' + tr(ROLES[p.role])}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, wordBreak: 'break-word' }}>
                    {p.email || '—'} · {ETATS[p.etat] || p.etat}
                  </div>
                  {p.telephone && (
                    <div style={{ fontSize: 12 }}>
                      📞 <a href={lienAppel(p.telephone)}>{p.telephone}</a>
                    </div>
                  )}
                </div>
                {p.invitation && p.etat === 'INVITE' && (
                  <button
                    onClick={() => agit(() => portailApi.annuleInvitation(p.id))}
                    style={{ ...bouton(C.red, false), padding: '6px 9px', fontSize: 12 }}
                  >
                    Annuler
                  </button>
                )}
                {equipier && (
                  <button
                    onClick={() => agit(() => portailApi.acces(p.id, p.etat !== 'ACTIF'))}
                    style={{
                      ...bouton(p.etat === 'ACTIF' ? C.red : C.green, false),
                      padding: '6px 9px',
                      fontSize: 12,
                    }}
                  >
                    {p.etat === 'ACTIF' ? 'Fermer l’accès' : 'Rouvrir'}
                  </button>
                )}
              </div>
              {equipier && (
                <select
                  aria-label={'Rôle de ' + nom}
                  value={p.role === 'CHEF' ? 'CHEF' : 'EMPLOYE'}
                  onChange={(e) => agit(() => portailApi.role(p.id, e.target.value))}
                  style={{ ...champ, marginTop: 6, fontSize: 13 }}
                >
                  {/* Le mot seul : la phrase entière était coupée sur un téléphone. */}
                  {CHOIX_ROLES.map(([cle]) => (
                    <option key={cle} value={cle}>
                      {tr(ROLES[cle])}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ ...carte, fontSize: 13, lineHeight: 1.6 }}>
        <b style={{ fontSize: 15 }}>Chacun son propre accès</b>
        <div>1. Chacun ouvre son propre lien (valable 24 heures, une seule fois).</div>
        <div>2. Il crée son compte : son e-mail, son mot de passe et son portable.</div>
        <div>
          3. À chaque connexion : son e-mail et son mot de passe. Un code en plus s’il le veut, dans
          « Mon compte ».
        </div>
        <div style={{ color: C.grey }}>
          Vous pouvez couper l’accès d’une personne à tout moment, sans toucher aux autres.
        </div>
      </div>
    </>
  );
}

/** Ce que devient une adresse d'une invitation groupée, dit simplement. */
const RESULTATS = {
  INVITE: ['Invitée ✓', '#146C3A'],
  DEJA_MEMBRE: ['Déjà dans l’équipe', '#475569'],
  ADRESSE_INVALIDE: ['Adresse invalide', '#B91C1C'],
};

/** Le libellé du résultat d'une adresse, chez combien de fournisseurs. */
function libelleResultat(r, plusieurs) {
  if (r.resultat === 'INVITE' && plusieurs && r.invitesChez > 0) {
    const invite = trf('Invitée chez {0} ✓', r.invitesChez);
    return r.dejaMembreChez > 0
      ? invite + ' · ' + trf('déjà dans l’équipe chez {0}', r.dejaMembreChez)
      : invite;
  }
  return tr((RESULTATS[r.resultat] || [r.resultat])[0]);
}

/**
 * « Inviter plusieurs personnes » : une liste d'adresses, un rôle, les
 * fournisseurs où inviter — tous cochés au départ —, un seul e-mail par
 * personne.
 */
function InvitationGroupee({ magasin, apres, ouvriersSeulement = false }) {
  const [texte, setTexte] = useState('');
  const [role, setRole] = useState('EMPLOYE');
  const [etat, setEtat] = useState({ enCours: false, erreur: '' });
  const [resultats, setResultats] = useState(null);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [coches, setCoches] = useState([]);
  useEffect(() => {
    let annule = false;
    Promise.resolve()
      .then(() => portailApi.fournisseurs())
      .then((l) => {
        if (annule || !Array.isArray(l)) return;
        setFournisseurs(l);
        setCoches(l.map((f) => f.id));
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, []);
  // Un chef s'invite chez les fournisseurs où l'on est responsable.
  const permis = fournisseurs.filter((f) => role !== 'CHEF' || f.role === 'RESPONSABLE');
  const choisis = permis.filter((f) => coches.includes(f.id));
  const plusieurs = fournisseurs.length > 1;
  const adresses = adressesDe(texte);
  const envoie = async (e) => {
    e.preventDefault();
    if (adresses.length === 0) {
      setEtat({ enCours: false, erreur: 'Collez au moins une adresse e-mail.' });
      return;
    }
    if (plusieurs && choisis.length === 0) {
      setEtat({ enCours: false, erreur: tr('Cochez au moins un fournisseur.') });
      return;
    }
    setEtat({ enCours: true, erreur: '' });
    try {
      const r = await portailApi.inviteParLot({
        emails: adresses,
        role,
        ...(fournisseurs.length ? { magasins: choisis.map((f) => f.id) } : {}),
      });
      setResultats(Array.isArray(r) ? r : []);
      setTexte('');
      setEtat({ enCours: false, erreur: '' });
      apres();
    } catch (err) {
      setEtat({ enCours: false, erreur: err?.message || 'Les invitations ne sont pas parties.' });
    }
  };
  return (
    <>
      <form style={carte} onSubmit={envoie}>
        <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>Inviter plusieurs personnes</h2>
        <label style={etiquette}>
          Adresses e-mail (une par ligne)
          <textarea
            rows={4}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder={['luca@exemple.ch', 'ana@exemple.ch'].join('\n')}
            style={{ ...champ, resize: 'vertical', fontSize: 14 }}
          />
        </label>
        <div style={{ marginTop: 8 }}>
          <ChoixRole
            value={role}
            onChange={setRole}
            label="Leur rôle"
            ouvriersSeulement={ouvriersSeulement}
          />
        </div>
        {plusieurs && (
          <fieldset
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              background: '#EEF6F7',
              border: '2px solid ' + C.teal,
              borderRadius: 12,
              padding: '10px 12px',
              margin: '10px 0 0',
            }}
          >
            <legend style={{ fontSize: 13, fontWeight: 800, color: '#0A5260', padding: '0 4px' }}>
              Chez quels fournisseurs ?
            </legend>
            {fournisseurs.map((f) => {
              const autorise = permis.includes(f);
              return (
                <label
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    minHeight: 32,
                    opacity: autorise ? 1 : 0.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={autorise && coches.includes(f.id)}
                    disabled={!autorise}
                    onChange={(ev) =>
                      setCoches((c) =>
                        ev.target.checked ? [...c, f.id] : c.filter((id) => id !== f.id),
                      )
                    }
                    style={{ width: 20, height: 20, flexShrink: 0 }}
                  />
                  <span style={{ overflowWrap: 'anywhere' }}>{f.nom}</span>
                </label>
              );
            })}
            <span style={{ fontSize: 12, color: C.grey, lineHeight: 1.45 }}>
              Tous cochés au départ. Décochez un magasin où la personne ne doit pas commander.
            </span>
          </fieldset>
        )}
        {etat.erreur && (
          <div role="alert" style={alerte}>
            {etat.erreur}
          </div>
        )}
        <button
          type="submit"
          disabled={etat.enCours}
          style={{ ...bouton(C.green), width: '100%', marginTop: 10 }}
        >
          {etat.enCours
            ? tr('Envoi…')
            : plusieurs && adresses.length > 0
              ? trf(
                  '✉️ Inviter {0} personne(s) chez {1} fournisseur(s)',
                  adresses.length,
                  choisis.length,
                )
              : adresses.length > 1
                ? trf('✉️ Envoyer les {0} invitations', adresses.length)
                : tr('✉️ Envoyer l’invitation')}
        </button>
      </form>
      {resultats && (
        <div style={carte} aria-label="Résultat des invitations">
          <h3 style={{ fontSize: 15, margin: '0 0 6px' }}>Résultat des invitations</h3>
          {resultats.map((r) => {
            const couleur = (RESULTATS[r.resultat] || [null, C.grey])[1];
            const lib = libelleResultat(r, plusieurs);
            return (
              <div
                key={r.email}
                data-testid={'resultat-' + r.email}
                style={{ padding: '8px 0', borderBottom: '1px solid ' + C.border }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, wordBreak: 'break-all' }}>{r.email}</span>
                  <b style={{ fontSize: 12.5, color: couleur, textAlign: 'right' }}>{lib}</b>
                </div>
                {r.resultat === 'INVITE' && r.lien && (
                  <BoutonsPartage
                    message={trf(
                      'Bonjour, voici votre lien pour commander chez {0} : {1}',
                      (plusieurs ? choisis.map((f) => f.nom).join(', ') : magasin) ||
                        tr('le magasin'),
                      r.lien,
                    )}
                  />
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.45 }}>
            {plusieurs
              ? tr(
                  'Chaque personne invitée reçoit un seul e-mail, avec un seul lien pour tous ces fournisseurs. Vous pouvez aussi le lui envoyer par WhatsApp.',
                )
              : tr(
                  'Chaque personne invitée reçoit son lien par e-mail. Vous pouvez aussi le lui envoyer par WhatsApp.',
                )}
          </div>
        </div>
      )}
    </>
  );
}

/** Les deux façons de partager un lien : WhatsApp, ou le copier. */
function BoutonsPartage({ message }) {
  const [copie, setCopie] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
      <a
        href={'https://wa.me/?text=' + encodeURIComponent(message)}
        target="_blank"
        rel="noreferrer"
        style={{
          ...bouton(C.green),
          flex: 1,
          textAlign: 'center',
          textDecoration: 'none',
          boxSizing: 'border-box',
        }}
      >
        💬 WhatsApp
      </a>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(message);
            setCopie(true);
          } catch {
            setCopie(false);
          }
        }}
        style={{ ...bouton(C.navy, false), flex: 1 }}
      >
        {copie ? '✓ Copié' : '📋 Copier'}
      </button>
    </div>
  );
}

/**
 * Le lien d'une invitation tout juste créée, à envoyer par WhatsApp ou à
 * copier ; parti par e-mail si une adresse a été donnée.
 *
 * @param {object} props propriétés
 * @param {object} props.invitee l'invitation créée
 * @returns {import('react').ReactElement} la carte de partage
 */
export function PartageInvitation({ invitee }) {
  return (
    <div style={{ ...carte, border: '1.5px solid ' + C.green }}>
      <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
        ✓ Invitation prête pour {invitee.personne?.prenom}
        {invitee.courrielEnvoye ? ' — envoyée par e-mail' : ''}
      </div>
      <div style={{ fontSize: 12, color: C.muted, wordBreak: 'break-all', marginBottom: 8 }}>
        {invitee.lien}
      </div>
      <BoutonsPartage message={invitee.message} />
    </div>
  );
}
